import { useState } from "react";
import { Plus, Pin, PinOff, Pencil, Trash2, Megaphone, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { PageHeader, LoadingGrid, ErrorState, EmptyState, Pager, UploadButton, Field, Confirm, Media } from "@/components/common";
import { Sel } from "@/components/admin-ui";
import { PostCard } from "@/pages/creator/Channel";
import { api, errMsg } from "@/lib/api";

const TYPES = [["announcement", "Announcement"], ["collaboration_update", "Collaboration update"], ["creator_update", "Creator update"], ["important", "Important information"], ["general", "General"]];

function PostEditor({ post, onClose, onSaved }) {
  const [f, setF] = useState(post || { title: "", body: "", post_type: "announcement", media: [], pinned: false });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { title: f.title, body: f.body, post_type: f.post_type, media: f.media, pinned: f.pinned };
      if (post) await api.put(`/admin/channel/${post.id}`, body); else await api.post("/admin/channel", body);
      toast.success(post ? "Post updated" : "Post published · creators notified");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="post-editor">
        <DialogHeader><DialogTitle className="font-display">{post ? "Edit post" : "New channel post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Type"><Sel value={f.post_type} onChange={(v) => setF({ ...f, post_type: v })} options={TYPES} testid="post-type-select" className="bg-background" /></Field>
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl bg-background" data-testid="post-title-input" /></Field>
          <Field label="Text"><Textarea rows={6} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="rounded-xl bg-background" data-testid="post-body-input" /></Field>
          {f.media.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {f.media.map((m, i) => (
                <div key={i} className="relative"><Media item={m} className="h-28" />
                  <button onClick={() => setF({ ...f, media: f.media.filter((_, j) => j !== i) })} className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/70 grid place-items-center"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <UploadButton label="Image" accept="image/*" onUploaded={(r) => setF({ ...f, media: [...f.media, { url: r.url, type: r.content_type }] })} testid="post-image-upload" />
            <UploadButton label="Video" accept="video/*" onUploaded={(r) => setF({ ...f, media: [...f.media, { url: r.url, type: r.content_type }] })} testid="post-video-upload" />
          </div>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.pinned} onCheckedChange={(v) => setF({ ...f, pinned: v })} data-testid="post-pin-switch" />Pin to top</label>
          <Button onClick={save} disabled={busy} className="rounded-full w-full" data-testid="post-publish-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : post ? "Save" : "Publish"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminChannel() {
  const [page, setPage] = useState(1);
  const [editor, setEditor] = useState(null);
  const { data, loading, error, reload } = useApi("/channel", { page });
  const pin = async (p) => { try { await api.put(`/admin/channel/${p.id}`, { pinned: !p.pinned }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (p) => { try { await api.delete(`/admin/channel/${p.id}`); toast.success("Post deleted"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-3xl">
      <PageHeader eyebrow="Official channel" title="Channel" desc="Admin-only posts. Creators can read but never post, comment or see the audience.">
        <Button className="rounded-full" onClick={() => setEditor({})} data-testid="new-post-button"><Plus className="h-4 w-4 mr-1.5" />New post</Button>
      </PageHeader>
      {loading ? <LoadingGrid count={3} className="space-y-4" h="h-40" /> : error ? <ErrorState message={error} onRetry={reload} /> :
        data.items.length === 0 ? <EmptyState icon={Megaphone} title="No announcements yet." testid="admin-channel-empty" /> : (
          <div className="space-y-4">
            {data.items.map((p) => (
              <PostCard key={p.id} post={p} full actions={<>
                <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => pin(p)} data-testid={`pin-post-${p.id}`}>{p.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}</Button>
                <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={() => setEditor({ post: p })} data-testid={`edit-post-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Confirm title="Delete this post?" destructive confirmLabel="Delete" onConfirm={() => del(p)} testid={`delete-post-confirm-${p.id}`}
                  trigger={<Button size="icon" variant="ghost" className="rounded-full h-8 w-8 text-red-400" data-testid={`delete-post-${p.id}`}><Trash2 className="h-4 w-4" /></Button>} />
              </>} />
            ))}
          </div>
        )}
      {data && <Pager page={page} total={data.total} limit={10} onPage={setPage} />}
      {editor && <PostEditor post={editor.post} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); reload(); }} />}
    </div>
  );
}
